/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from "@emotion/react";
import React, { useEffect, useRef, useState } from "react";
import { CLEAR_BUTTON } from "../../styles/styles";
import { ReactComponent as Ellipsis } from "../../assets/icons/ellipsisVerticalSolid.svg";
import { LIGHT_GREY, MIDDLE_GREY, WHITE } from "../../styles/colors";

const container = css`
  position: relative;
  display: flex;
  align-items: center;
  user-select: none;  
`;

const menuItem = (disabled?: boolean, isFocused?: boolean) => css`
  display: flex;
  align-items: center;
  font-size: 14px;
  width: 95%;
  gap: 8px;
  padding: 4px;
  cursor: ${disabled ? "not-allowed" : "pointer"};
  opacity: ${disabled ? 0.5 : 1};
  background-color: ${isFocused ? LIGHT_GREY : "transparent"};
  outline: none;

  :focus {
    outline: none;
  }

  :hover {
    background-color: ${!disabled ? LIGHT_GREY : "transparent"};
  }
`;

const popupMenu = (show: boolean, menuWidth: number | "auto") => css`
  visibility: ${show ? "visible" : "hidden"};
  position: absolute;
  top: calc(100% + 4px);
  left: -150%;
  transform: translateX(-60%);
  width: ${menuWidth === "auto" ? "fit-content" : `${menuWidth}px`};
  min-width: 115px;
  max-width: 240px;
  background-color: white;
  padding: 0.5rem;
  border: 1px solid ${WHITE};
  border-radius: 0.5rem;
  z-index: 2;
  box-shadow: 2px 3px 5px ${MIDDLE_GREY};
`;

const moreButton = (show: boolean) => css`
  display: inline-block;
  cursor: pointer;
  border-radius: 0.5rem;
  ${show ? `background-color: #373A365C;` : ``};
`;

const iconButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 0;
`;

const actionIcon = css`
  display: flex;
  align-items: center;

  & > * {
    width: 20px;
    height: 20px;
  }
`;

const actionLabel = css`
  white-space: nowrap;
`;

export type MenuAction = {
  id: string;
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  title?: string;
  disabled?: boolean;
  hidden?: boolean;
};

type Props = {
  actions: MenuAction[];
  menuWidth?: number | "auto";
  moreOptionsTitle?: string;
  rootCss?: SerializedStyles;
  closeOnClick?: boolean;
};

export const ActionMenu = ({
  actions,
  menuWidth = "auto",
  moreOptionsTitle,
  rootCss,
  closeOnClick = true,
}: Props) => {
  const [show, setShow] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const visibleActions = actions.filter((a) => !a.hidden);

  useEffect(() => {
    if (!show) {
      setFocusedIndex(-1);
    }
  }, [show]);

  // Focus first item when opening
  useEffect(() => {
    if (show && visibleActions.length > 0) {
      setFocusedIndex(0);
      itemRefs.current[0]?.focus();
    }
  }, [show, visibleActions.length]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!show) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) => {
          const next = (prev + 1) % visibleActions.length;
          itemRefs.current[next]?.focus();
          return next;
        });
        break;

      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) => {
          const next =
            (prev - 1 + visibleActions.length) % visibleActions.length;
          itemRefs.current[next]?.focus();
          return next;
        });
        break;

      case "Enter":
      case " ":
        e.preventDefault();
        if (focusedIndex >= 0) {
          const action = visibleActions[focusedIndex];
          if (!action.disabled) {
            action.onClick();
            if (closeOnClick) setShow(false);
          }
        }
        break;

      case "Escape":
        setShow(false);
        break;
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Close only if focus leaves the menu completely
    if (!menuRef.current?.contains(e.relatedTarget as Node)) {
      setShow(false);
    }
  };

  return (
    <div css={[container, rootCss]} onKeyDown={handleKeyDown}>
      {/* Menu */}
      <div
        ref={menuRef}
        css={popupMenu(show, menuWidth)}
        role="menu"
        onBlur={handleBlur}
      >
        {visibleActions.map((action, index) => (
          <div
            key={action.id}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            css={menuItem(action.disabled, focusedIndex === index)}
            role="menuitem"
            tabIndex={-1}
            title={action.title}
            onMouseEnter={() => setFocusedIndex(index)}
            onClick={() => {
              if (action.disabled) return;
              action.onClick();
              if (closeOnClick) setShow(false);
            }}
          >
            <button css={CLEAR_BUTTON} disabled={action.disabled}>
              {action.icon && <span css={actionIcon}>{action.icon}</span>}
            </button>
            <span css={actionLabel}>{action.label}</span>
          </div>
        ))}
      </div>

      <div
        css={moreButton(show)}
        title={moreOptionsTitle}
        onClick={() => setShow((prev) => !prev)}
      >
        <button css={[CLEAR_BUTTON, iconButton]}>{<Ellipsis />}</button>
      </div>
    </div>
  );
};
